import { DictionaryTypeDto } from '@/api/generated';
import { FormField } from '@/components/form/FormField.comp.tsx';
import { SelectSearchOptionOptionalModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { SearchableUniversityField } from '@/components/form/SearchableUniversityField';
import { useDictionariesContext } from '@/utils/providers/dictionariesContext/Dictionaries.context.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import Dropdown from '@/views/process/components/personalDataForm/Dropdown.tsx';
import {
  isStudentStatus,
  UserPersonalDataProperties,
} from '@/views/process/pages/personalDataForm/usePersonalDataForm.validation.ts';
import React from 'react';
import { Form } from 'react-bootstrap';
import { UseFormReturn } from 'react-hook-form';

interface Props {
  formMethods: UseFormReturn<UserPersonalDataProperties>;
}

const DetailSection: React.FC<Props> = ({ formMethods }) => {
  const {
    clearErrors,
    formState: { errors },
    register,
    setValue,
    watch,
  } = formMethods;
  const { t } = useTranslationWithPrefix('views.process.personalData');
  const dictionaries = useDictionariesContext();

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors('phoneNumber');
    const formattedValue = e.target.value.replace(/\D/g, '');
    if (formattedValue.length <= 15) {
      setValue('phoneNumber', formattedValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof UserPersonalDataProperties;
    setValue(fieldName, value);
    if (value && errors[fieldName]) {
      clearErrors(fieldName);
    }
  };

  const handleSelectChange = (option: { name: string; value?: string }) => {
    const name = option.name as keyof UserPersonalDataProperties;

    const dictionaryFieldMap: Record<string, DictionaryTypeDto> = {
      location: DictionaryTypeDto.Location,
      studentStatus: DictionaryTypeDto.Status,
      typeOfStudy: DictionaryTypeDto.TypeOfStudy,
      yearOfStudy: DictionaryTypeDto.YearOfStudy,
    };

    const dictionaryType = dictionaryFieldMap[name];

    if (dictionaryType && option.value) {
      const fullOption = dictionaries[dictionaryType]?.find(item => item.value === option.value);

      if (fullOption) {
        setValue(name, fullOption);
      }
    } else {
      setValue(name, option.value);
    }

    clearErrors(name);
  };

  const handleUniversityChange = (result: {
    customUniversity?: string;
    university?: SelectSearchOptionOptionalModel;
  }) => {
    if (result.university) {
      setValue('university', result.university);
      setValue('universityCustom', undefined);
    } else if (result.customUniversity) {
      setValue('university', undefined);
      setValue('universityCustom', result.customUniversity);
    } else {
      setValue('university', undefined);
      setValue('universityCustom', undefined);
    }
    clearErrors('university');
    clearErrors('universityCustom');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-3">
      <FormField
        errors={errors}
        label={t('fields.firstName')}
        name="firstName"
        onChange={handleInputChange}
        placeholder={t('fields.firstName')}
        register={register}
        required
        type="text"
      />
      <FormField
        errors={errors}
        label={t('fields.lastName')}
        name="lastName"
        onChange={handleInputChange}
        placeholder={t('fields.lastName')}
        register={register}
        required
        type="text"
      />
      <FormField
        errors={errors}
        label={t('fields.phoneNumber')}
        name="phoneNumber"
        onChange={handlePhoneNumberChange}
        placeholder={t('fields.phoneNumber')}
        register={register}
        required
        type="text"
      />
      <Form.Group className="mb-3 min-w-[290px] w-auto md:min-w-[320px] ">
        <Dropdown
          highlightFirst
          isInvalid={!!errors.location}
          label={t('fields.location.label')}
          name="location"
          onChange={handleSelectChange}
          options={dictionaries[DictionaryTypeDto.Location].map(item => ({
            id: item.value,
            label: item.label,
            value: item.value,
          }))}
          placeholder={t('fields.location.placeholder')}
          required
          value={watch('location')?.value}
        />
        {errors.location?.message && (
          <p className="dropdown-invalid small fw-bold">{errors.location?.message}</p>
        )}
      </Form.Group>
      <Form.Group className="mb-3">
        <Dropdown
          isInvalid={!!errors.studentStatus}
          label={t('fields.studentStatus')}
          name="studentStatus"
          onChange={handleSelectChange}
          options={dictionaries[DictionaryTypeDto.Status].map(item => ({
            id: item.value,
            label: item.label,
            value: item.value,
          }))}
          placeholder={t('fields.studentStatus')}
          required
          value={watch('studentStatus')?.value}
        />
        {errors.studentStatus?.message && (
          <p className="dropdown-invalid small fw-bold">{errors.studentStatus?.message}</p>
        )}
      </Form.Group>
      {(() => {
        const isStudent = isStudentStatus(watch('studentStatus'));

        return (
          <Form.Group className="mb-3">
            <Dropdown
              disabled={!isStudent}
              isInvalid={!!errors.yearOfStudy}
              label={t('fields.graduationYear.label')}
              name="yearOfStudy"
              onChange={handleSelectChange}
              options={dictionaries[DictionaryTypeDto.YearOfStudy].map(item => ({
                id: item.value,
                label: item.label,
                value: item.value,
              }))}
              placeholder={t('fields.graduationYear.placeholder')}
              required={isStudent}
              value={watch('yearOfStudy')?.value}
            />
            {errors.yearOfStudy?.message && (
              <p className="dropdown-invalid small fw-bold">{errors.yearOfStudy?.message}</p>
            )}
          </Form.Group>
        );
      })()}
      <SearchableUniversityField
        customUniversity={watch('universityCustom')}
        isInvalid={!!(errors.university || errors.universityCustom)}
        label={t('fields.universityName')}
        onChange={handleUniversityChange}
        placeholder={t('fields.universityName')}
        required
        selectedUniversity={watch('university')}
        universities={dictionaries[DictionaryTypeDto.UniversityName]}
      />
      {(errors.university?.message || errors.universityCustom?.message) && (
        <p className="dropdown-invalid small fw-bold">
          {errors.university?.message || errors.universityCustom?.message}
        </p>
      )}
      <FormField
        errors={errors}
        label={t('fields.fieldOfStudy')}
        name="fieldOfStudy"
        onChange={handleInputChange}
        placeholder={t('fields.fieldOfStudy')}
        register={register}
        required
        type="text"
      />
      <Form.Group className="mb-3 min-w-[290px] w-auto md:min-w-[320px] ">
        <Dropdown
          isInvalid={!!errors.typeOfStudy}
          label={t('fields.typeOfStudy')}
          name="typeOfStudy"
          onChange={handleSelectChange}
          options={dictionaries[DictionaryTypeDto.TypeOfStudy].map(item => ({
            id: item.value,
            label: item.label,
            value: item.value,
          }))}
          placeholder={t('fields.typeOfStudy')}
          required
          value={watch('typeOfStudy')?.value}
        />
        {errors.typeOfStudy?.message && (
          <p className="dropdown-invalid small fw-bold">{errors.typeOfStudy?.message}</p>
        )}
      </Form.Group>
      <FormField
        errors={errors}
        label={t('fields.githubUrl')}
        name="githubUrl"
        onChange={handleInputChange}
        placeholder={`${t('fields.githubUrl')} (${t('fields.notRequired')})`}
        register={register}
      />
      <FormField
        errors={errors}
        label={t('fields.linkedinUrl')}
        name="linkedInUrl"
        onChange={handleInputChange}
        placeholder={`${t('fields.linkedinUrl')} (${t('fields.notRequired')})`}
        register={register}
      />
    </div>
  );
};

export default DetailSection;
